/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./LayoutBaseView',
	'jquery'
], (BaseClass, $) => {

	/*
	* The generic page class is a view which can represent a relative or absolute layout	*/
	const ASPECT_RATIO_STYLE_ID = 'dashboardRelativeAspectRatio';

	const GenericPage = BaseClass.extend({

		aspectRatio: 0,

		init: function(/*options*/) {
			GenericPage.inherited('init', this, arguments);

			this.layoutController.topLayoutModel.on('change:pageSize', this._onChangePageSize, this);
			this.layoutController.topLayoutModel.on('change:fitPage', this._onFitPage, this);
			this.layoutController.topLayoutModel.on('change:layoutPositioning', this._onChangeLayoutPositioning, this);
			this._setPageLayoutStyling();
		},

		/**
		 * Called when the view is destroyed
		 */
		destroy() {
			this.layoutController.topLayoutModel.off('change:pageSize', this._onChangePageSize, this);
			this.layoutController.topLayoutModel.off('change:fitPage', this._onFitPage, this);
			this.layoutController.topLayoutModel.off('change:layoutPositioning', this._onChangeLayoutPositioning, this);

			// If this is the last generic page, clean up any page size styles
			let layoutModels = this.layoutController.topLayoutModel.findDescendantsWithType('genericPage');
			if (layoutModels.length === 0) {
				this._clearPageSize();
			}

			GenericPage.inherited('destroy', this, arguments);
		},

		/**
		 * Invoked when a hidden layout is shown
		 */
		onShow() {
			GenericPage.inherited('onShow', this, arguments);

			// Update page size when shown, as the 1 aspect ratio style may be different from the previous shown view
			if (this.$el.is(':visible')) {
				this._setPageFitStyling();
				this._applyPageSize();
				this._setPageLayoutStyling();
			}
		},

		/**
		* Apply the current page size configuration to the view
		*/
		_applyPageSize() {
			const layoutPositioning = this._getLayoutPositioning();
			if (layoutPositioning === 'absolute') {
				let pageSize = this.model.getValueFromSelfOrParent('pageSize');
				this._setAbsolutePageSize(pageSize);
			} else if (layoutPositioning === 'relative') {
				let pageSize = this.model.getValueFromSelfOrParent('pageSize');
				this._setRelativeAspectRatio(pageSize);
			}
		},

		/**
		* Clear page size settings from the view
		*/
		_clearPageSize(layoutPos) {
			let layoutPositioning = layoutPos || this._getLayoutPositioning();
			if (layoutPositioning === 'absolute') {
				this.$el.css({ width: '', height: '' });
			} else if (layoutPositioning === 'relative') {
				this._removeDynamicStyles(ASPECT_RATIO_STYLE_ID);
			}
		},

		_getLayoutPositioning() {
			return this.model.getLayoutPositioning(false);
		},

		_onChangePageSize(payload = {}) {
			// Remove previous page size remnants from the consumeView
			if (this.$el.is(':visible')) {
				this._clearPageSize(payload.prevValue);
				this._applyPageSize();
			}
		},

		_onFitPage() {
			if (this.$el.is(':visible')) {
				this._setPageFitStyling();
				this._onChangePageSize();
			}

			this.onResize();
		},

		_onChangeLayoutPositioning(payload) {
			this._clearPageSize(payload.prevValue);

			if (this.$el.is(':visible')) {
				this._setPageLayoutStyling();
				this._setPageFitStyling();
				// Remove previous page size remnants
				this._applyPageSize();
			}

			this.onResize();
		},


		_getFitPage: function() {
			let fitPage = false;
			if (this.model.getValueFromSelfOrParent('layoutPositioning') !== 'absolute') {
				fitPage = this.model.getValueFromSelfOrParent('fitPage') || false;
			}
			return fitPage;
		},

		_setPageFitStyling() {
			let isRelative = this._getLayoutPositioning() === 'relative';
			const fitPage = isRelative && this._getFitPage();
			this.$el.toggleClass('fitToPage', fitPage);
		},

		_setPageLayoutStyling() {
			let isRelative = this._getLayoutPositioning() === 'relative';
			this.$el.toggleClass('templateBox', isRelative);
		},

		_setAbsolutePageSize(pageSize) {
			this.$el.width(pageSize.width);
			this.$el.height(pageSize.height);
		},

		_setRelativeAspectRatio(pageSize) {
			this.aspectRatio = pageSize.height / pageSize.width;

			const fitPage = this._getFitPage();
			if (fitPage) {
				this._addAspectRatioForFitToPage();
			} else {
				// Fit to Width
				this._createStyleNode(`.aspectRatio_default:before { padding-top:${this.aspectRatio * 100}%; }`, ASPECT_RATIO_STYLE_ID);
			}
		},

		onResize: function() {
			if (this.$el.is(':visible')) {
				const fitPage = this._getFitPage();
				if (fitPage) {
					this._addAspectRatioForFitToPage();
				}
			}
			GenericPage.inherited('onResize', this, arguments);
		},

		_addAspectRatioForFitToPage: function(){
			let size  = { 'height': this.$el.parent().height(), 'width': this.$el.parent().width() };
			let style = '.page.pagecontainer .pagegenericPage { width: '+size.width+'px; height:'+this.aspectRatio * size.width+'px; max-height: '+size.height+'px; max-width:'+((1/this.aspectRatio)*size.height)+'px;}';
			this._createStyleNode(style, ASPECT_RATIO_STYLE_ID);
		},

		_createStyleNode(styles, stylesId) {
			let dynamicAspectRatioStyle = $(`#${stylesId}`).html();
			if (dynamicAspectRatioStyle !== styles) {
				this._removeDynamicStyles(stylesId);

				const styleNode = $(`<style class='dashboardLayoutStyles' type="text/css" id="${stylesId}"></style>`);
				styleNode.html(styles);
				styleNode.appendTo('head');
			}
		},

		_removeDynamicStyles(stylesId) {
			$(`#${stylesId}`).remove();
		}

	});

	return GenericPage;
});
